// Editing a text file in place.
//
// Saving files a NEW VERSION rather than overwriting, so an edit made here carries the same history as
// an edit made in Word and uploaded: who changed it, when, and what it said before. A file too big to
// hold in the browser opens read-only — saving a clipped copy would throw the rest of it away.

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { Button } from '/imports/ui/Button';
import { Input } from '/imports/ui/Input';
import { Modal, ModalStyled } from '/imports/ui/Modal';
import { toastOnError } from '/imports/ui/utils';
import { useMethod } from '/imports/hooks';
import { driveSaveText, driveTextContent } from '/imports/api/drive';

const DriveTextEditorModalStyled = styled(ModalStyled)`
  .modal-window { width: 80vw; max-width: min(60rem, 100%); }
  textarea {
    width: 100%; height: 55vh; box-sizing: border-box; resize: vertical;
    font-family: monospace; font-size: 0.85rem; line-height: 1.45;
    border: 1px solid ${css.grey7}; border-radius: ${css.inputBorderRadius}; padding: 0.6rem;
  }
  textarea:focus { outline: none; border-color: ${css.primaryColor}; }
  textarea:disabled { background: ${css.grey9}; color: ${css.grey2}; }
  .editor-foot { display: flex; align-items: center; gap: 0.5rem; margin-top: 0.75rem; }
  .editor-foot .note { flex: 1; }
  .editor-foot .hint { color: ${css.grey4}; font-size: 0.78rem; }
`;

export interface DriveTextEditorModalProps {
  _id: string;
  name: string;
  onClose(): void;
  onSaved(): void;
}

export const DriveTextEditorModal = (props: DriveTextEditorModalProps) => {
  const [content, loading] = useMethod(driveTextContent, { _id: props._id }, [props._id]);
  const [text, setText] = useState<string>();
  const [note, setNote] = useState<string>();
  const [saving, setSaving] = useState(false);
  // The loaded file is the starting point; `text` holds the edit, so an unopened file is not "changed".
  useEffect(() => setText(content?.text), [content?.text]);
  const dirty = content && text !== undefined && text !== content.text;

  const save = toastOnError(async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      await driveSaveText.call({ _id: props._id, text: text || '', changeNote: note?.trim() || undefined });
      props.onSaved();
      props.onClose();
    }
    finally {
      setSaving(false);
    }
  });

  return (
    <Modal
      styled={DriveTextEditorModalStyled}
      title={props.name}
      onClose={props.onClose}
      buttons={content?.editable ? (
        <>
          <Button className="secondary" onClick={props.onClose}>{strings.Cancel()}</Button>
          <Button disabled={!dirty || saving} onClick={() => save()}>{strings.Save()}</Button>
        </>
      ) : undefined}
    >
      {loading && !content && <div className="muted">{strings.Loading()}</div>}
      {content && (
        <>
          <textarea
            value={text ?? ''}
            disabled={!content.editable || saving}
            spellCheck={false}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="editor-foot">
            {content.editable ? (
              <>
                <Input
                  className="note"
                  placeholder={strings.ChangeNote()}
                  value={note || ''}
                  onChange={(value) => setNote(value)}
                />
                <span className="hint">{strings.DriveSaveMakesVersion()}</span>
              </>
            ) : (
              <span className="hint">{content.truncated ? strings.DriveTextTruncated() : strings.AccessViewer()}</span>
            )}
          </div>
        </>
      )}
    </Modal>
  );
};
